import * as React from "react";
import { ReactNode } from "react";
import {
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { User as SelectUser } from "@shared/schema";
import { queryClient } from "../lib/queryClient";
import { useToast } from "@/hooks/use-toast";

type LoginData = {
  username: string;
  password: string;
};

type RegisterData = {
  username: string;
  password: string;
  email?: string;
  friendlyName?: string;
  companyName: string;
  city?: string;
  state?: string;
  phone?: string;
  googlePlaceId?: string;
  googleMapsUrl?: string;
};

type ClaimAccountData = {
  slug: string;
  verifyValue: string;
  pin: string;
};

type SetPasswordData = {
  password: string;
};

type SponsorInfo = {
  name: string;
  phone: string | null;
  vcardUrl: string | null;
  message: string | null;
  claimed: boolean;
  // Self-service sponsorship fields
  sponsorUserId?: number;
  sponsorSlug?: string | null;
  sponsorReviewSlugCode?: string | null;
  engagementStage?: string | null;
  sponsorshipEndDate?: string | null;
  hasLeftSponsorReview?: boolean;
} | null;

type Impersonator = { id: number; username: string; friendlyName: string | null } | null;

type AuthContextType = {
  user: SelectUser | null;
  isLoading: boolean;
  error: Error | null;
  sponsor: SponsorInfo;
  needsPasswordReset: boolean;
  impersonator: Impersonator;
  loginMutation: UseMutationResult<SelectUser, Error, LoginData>;
  logoutMutation: UseMutationResult<void, Error, void>;
  registerMutation: UseMutationResult<{ user: SelectUser; skipSetup?: boolean }, Error, RegisterData>;
  claimAccountMutation: UseMutationResult<any, Error, ClaimAccountData>;
  setPasswordMutation: UseMutationResult<any, Error, SetPasswordData>;
  isAdmin: () => boolean;
  isOwner: () => boolean;
  isTechnician: () => boolean;
  canManageUsers: () => boolean;
  canAccessSettings: () => boolean;
};

export const AuthContext = React.createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const [user, setUser] = React.useState<SelectUser | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const [sponsor, setSponsor] = React.useState<SponsorInfo>(null);
  const [needsPasswordReset, setNeedsPasswordReset] = React.useState(false);
  const [impersonator, setImpersonator] = React.useState<Impersonator>(null);

  // Check authentication on mount
  React.useEffect(() => {
    let isMounted = true;

    async function checkAuth() {
      try {
        setIsLoading(true);
        const res = await fetch('/api/user', {
          credentials: 'include',
        });

        if (!isMounted) return;

        if (res.ok) {
          const data = await res.json();
          setUser(data.user);
          setSponsor(data.sponsor || null);
          setNeedsPasswordReset(data.needsPasswordReset || false);
          setImpersonator(data.impersonator || null);
          setError(null);
        } else {
          setUser(null);
          setImpersonator(null);
          setError(null);
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('[Auth] Check auth error:', err);
        setUser(null);
        setError(err instanceof Error ? err : new Error('Auth check failed'));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    checkAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginData) => {
      const res = await fetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(credentials),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Login failed');
      }

      return await res.json();
    },
    onSuccess: (data) => {
      setUser(data.user);
      setError(null);
      queryClient.setQueryData(["/api/user"], data.user);
    },
    onError: (error: Error) => {
      setError(error);
      toast({
        title: "Login failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (data: RegisterData) => {
      const res = await fetch('/api/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Registration failed');
      }

      return await res.json();
    },
    onSuccess: (data) => {
      setUser(data.user);
      setError(null);
      queryClient.setQueryData(["/api/user"], data.user);
      toast({
        title: "Account created",
        description: "Welcome to AIReview!",
      });
    },
    onError: (error: Error) => {
      setError(error);
      toast({
        title: "Registration failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/logout', {
        method: 'POST',
        credentials: 'include',
      });

      if (!res.ok) {
        throw new Error('Logout failed');
      }
    },
    onSuccess: () => {
      setUser(null);
      setError(null);
      queryClient.clear();
    },
    onError: (error: Error) => {
      toast({
        title: "Logout failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const claimAccountMutation = useMutation({
    mutationFn: async (data: ClaimAccountData) => {
      const res = await fetch('/api/claim-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Verification failed');
      }

      return await res.json();
    },
    onSuccess: (data) => {
      setUser(data.user);
      setNeedsPasswordReset(data.needsPasswordReset || false);
      setError(null);
    },
    onError: (error: Error) => {
      setError(error);
      toast({
        title: "Verification failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const setPasswordMutation = useMutation({
    mutationFn: async (data: SetPasswordData) => {
      const res = await fetch('/api/set-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to set password');
      }

      return await res.json();
    },
    onSuccess: () => {
      setNeedsPasswordReset(false);
      toast({
        title: "Password set",
        description: "Your account is ready to use!",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to set password",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const isAdmin = () => user?.role === 'admin';
  const isOwner = () => user?.role === 'owner';
  const isTechnician = () => user?.role === 'technician';
  const canManageUsers = () => ['admin', 'owner'].includes(user?.role || '');
  const canAccessSettings = () => ['admin', 'owner'].includes(user?.role || '');

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        error,
        sponsor,
        needsPasswordReset,
        impersonator,
        loginMutation,
        logoutMutation,
        registerMutation,
        claimAccountMutation,
        setPasswordMutation,
        isAdmin,
        isOwner,
        isTechnician,
        canManageUsers,
        canAccessSettings,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
